import { useState } from "react";
import { Reveal } from "./Reveal";
import { HeartHandshake, Repeat } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { DONATE_OPTIONS } from "@/i18n/extra_translations";

const STRIPE_URL = "https://donate.stripe.com/fZu6oH4pWaH35D9dxJ48000";
const PAYPAL_URL = "https://www.paypal.com/donate/?hosted_button_id=Q93DWWCCP4R9U";
const PRESETS = [
  { label: "$25", once: "donation_25", monthly: "donation_25_monthly" },
  { label: "$50", once: "donation_50", monthly: "donation_50_monthly" },
  { label: "$100", once: "donation_100", monthly: "donation_100_monthly" },
  { label: "$250", once: "donation_250", monthly: "donation_250_monthly" },
];

const Donate = () => {
  const { t, lang } = useLanguage();
  const opts = DONATE_OPTIONS[lang] || DONATE_OPTIONS.en;
  const [frequency, setFrequency] = useState("once");
  const [selected, setSelected] = useState(1);
  const [custom, setCustom] = useState("");

  const monthly = frequency === "monthly";

  return (
    <section id="donate" className="py-28 lg:py-36" data-testid="donate-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-12 gap-6 lg:gap-12 items-center">
        <Reveal className="col-span-12 lg:col-span-6">
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C7368] mb-6">{t("don_kicker")}</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1A2F24]">
            {t("don_title_a")}<em className="text-[#F4A261]">{t("don_title_em")}</em>{t("don_title_b")}
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-[#5C7368]">
            {t("don_body")}
          </p>
          <p className="mt-4 text-xs tracking-[0.15em] uppercase text-[#5C7368]">
            {t("don_ein")}
          </p>
        </Reveal>
        <Reveal delay={0.15} className="col-span-12 lg:col-span-6">
          <div className="rounded-3xl border border-[#E2E8E4] bg-[#F2F4F0] p-8 sm:p-10" data-testid="donate-card">
            <div className="grid grid-cols-2 gap-2 rounded-full bg-white/70 border border-[#E2E8E4] p-1.5" data-testid="donate-frequency-toggle">
              <button onClick={() => setFrequency("once")} data-testid="donate-frequency-once"
                className={`rounded-full py-2.5 text-xs tracking-[0.12em] uppercase font-semibold transition-all duration-300 ${!monthly ? "bg-[#1E352A] text-[#FAF9F6]" : "text-[#5C7368] hover:text-[#1A2F24]"}`}>
                {t("don_once")}
              </button>
              <button onClick={() => setFrequency("monthly")} data-testid="donate-frequency-monthly"
                className={`rounded-full py-2.5 text-xs tracking-[0.12em] uppercase font-semibold transition-all duration-300 inline-flex items-center justify-center gap-1.5 ${monthly ? "bg-[#F4A261] text-white" : "text-[#5C7368] hover:text-[#1A2F24]"}`}>
                <Repeat size={13} /> {t("don_monthly")}
              </button>
            </div>
            <div className="grid grid-cols-4 gap-3 mt-5">
              {PRESETS.map((p, i) => (
                <button key={p.label} data-testid={`donate-preset-${p.once}`}
                  onClick={() => { setSelected(i); setCustom(""); }}
                  className={`rounded-full py-3 text-sm font-semibold transition-all duration-300 ${
                    selected === i && !custom
                      ? "bg-[#1E352A] text-[#FAF9F6]"
                      : "bg-white/70 text-[#1E352A] border border-[#E2E8E4] hover:border-[#F4A261]"
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="mt-4 relative">
              <span className="absolute left-5 top-1/2 -translate-y-1/2 text-[#5C7368]">$</span>
              <input
                type="number" min="1" placeholder={t("don_custom")}
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                data-testid="donate-custom-amount"
                className="w-full rounded-full bg-white/70 border border-[#E2E8E4] py-3.5 pl-9 pr-5 text-sm text-[#1A2F24] placeholder:text-[#5C7368]/60 focus:outline-none focus:border-[#F4A261] transition-colors duration-300"
              />
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a href={STRIPE_URL} target="_blank" rel="noopener noreferrer" data-testid="donate-stripe-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F4A261] text-white py-4 text-xs sm:text-sm tracking-[0.1em] uppercase font-semibold transition-all duration-300 hover:bg-[#1E352A]">
                <HeartHandshake size={16} /> {opts.stripe}
              </a>
              <a href={PAYPAL_URL} target="_blank" rel="noopener noreferrer" data-testid="donate-paypal-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#003087] text-white py-4 text-xs sm:text-sm tracking-[0.1em] uppercase font-semibold transition-all duration-300 hover:bg-[#1E352A]">
                <Repeat size={16} /> {opts.paypal}
              </a>
            </div>
            <p className="mt-4 text-center text-[11px] text-[#5C7368]">
              {opts.note}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Donate;
